import React, { useMemo, useState } from "react";
import { Box, ButtonBase, Divider, Popover, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";

const displayMetadataValue = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const MetadataChangesPopover = ({ column }) => {
  const [anchorElement, setAnchorElement] = useState(null);
  const changes = useMemo(
    () =>
      Object.entries(column?.metadata_changes || {}).map(([key, change]) => ({
        key,
        label: change?.label || key.replace(/_/g, " "),
        oldValue: displayMetadataValue(change?.old),
        newValue: displayMetadataValue(change?.new),
      })),
    [column?.metadata_changes],
  );
  const isOpen = Boolean(anchorElement);

  return (
    <>
      <ButtonBase
        onClick={(event) => setAnchorElement(event.currentTarget)}
        aria-label={`View metadata changes for ${column?.column_name}`}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        sx={{
          color: "inherit",
          font: "inherit",
          fontWeight: "inherit",
          gap: 0.75,
          textAlign: "center",
        }}
      >
        {column?.column_name}
        <InfoOutlinedIcon sx={{ fontSize: 16, flexShrink: 0 }} />
      </ButtonBase>
      <Popover
        open={isOpen}
        anchorEl={anchorElement}
        onClose={() => setAnchorElement(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        slotProps={{
          paper: {
            sx: {
              width: 390,
              maxWidth: "calc(100vw - 32px)",
              border: "1px solid #E0B23E",
              borderRadius: 2,
              p: 1.5,
            },
          },
        }}
      >
        <Box display="flex" alignItems="center" gap={0.75} mb={1}>
          <EditNoteOutlinedIcon sx={{ color: "#B7791F" }} />
          <Typography fontSize={14} fontWeight={700}>
            Metadata changed
          </Typography>
        </Box>
        {changes.length ? (
          changes.map((change, index) => (
            <Box key={change.key}>
              {index > 0 && <Divider sx={{ my: 1 }} />}
              <Box
                display="grid"
                gridTemplateColumns="80px minmax(0, 1fr)"
                columnGap={1}
                rowGap={0.75}
              >
                <Typography color="text.secondary" fontSize={13}>
                  Field
                </Typography>
                <Typography fontSize={13} fontWeight={600}>
                  {change.label}
                </Typography>
                <Typography color="text.secondary" fontSize={13}>
                  Old
                </Typography>
                <Typography
                  color="text.secondary"
                  fontSize={13}
                  sx={{
                    overflowWrap: "anywhere",
                    textDecoration: "line-through",
                  }}
                >
                  {change.oldValue}
                </Typography>
                <Typography color="text.secondary" fontSize={13}>
                  New
                </Typography>
                <Typography
                  fontSize={13}
                  fontWeight={600}
                  sx={{ color: "#854D0E", overflowWrap: "anywhere" }}
                >
                  {change.newValue}
                </Typography>
              </Box>
            </Box>
          ))
        ) : (
          <Typography fontSize={13} color="text.secondary">
            {column?.change_details || "Metadata was changed."}
          </Typography>
        )}
      </Popover>
    </>
  );
};

export default MetadataChangesPopover;
